// This file is generated by generate-bindings. Do not edit manually.
import { z } from "zod";
import { DateTime } from "luxon";

export const DateStringSchema = z
  .string()
  .refine((v) => DateTime.fromFormat(v, "yyyy-MM-dd").isValid)
  .brand<"DateString">();
export type DateString = z.infer<typeof DateStringSchema>;

export const isDateString = (val: string): val is DateString =>
  DateTime.fromFormat(val, "yyyy-MM-dd").isValid;
export const makeDateString = (val: string): DateString => {
  if (isDateString(val)) return val;
  throw new Error("Invalid date string: " + val);
};

export const DateTimeStringSchema = z
  .string()
  .refine((v) => DateTime.fromISO(v).isValid)
  .brand<"DateTimeString">();
export type DateTimeString = z.infer<typeof DateTimeStringSchema>;

export const isDateTimeString = (val: string): val is DateTimeString =>
  DateTime.fromISO(val).isValid;
export const makeDateTimeString = (val: string): DateTimeString => {
  if (isDateTimeString(val)) return val;
  throw new Error("Invalid date time string: " + val);
};

export const TransactionIdSchema = z.string();
export type TransactionId = z.infer<typeof TransactionIdSchema>;

export const SmallStrSchema = z.string();
export type SmallStr = z.infer<typeof SmallStrSchema>;

export const SourceSchema = z.lazy(() => SmallStrSchema);
export type Source = z.infer<typeof SourceSchema>;

export const GroupSchema = z.lazy(() => SmallStrSchema);
export type Group = z.infer<typeof GroupSchema>;

export const CategorySchema = z.lazy(() => SmallStrSchema);
export type Category = z.infer<typeof CategorySchema>;

export const CurrencySchema = z.enum(["NOK", "SEK", "USD", "EUR", "DKK", "AED", "THB"]);
export type Currency = z.infer<typeof CurrencySchema>;

export const MoneySchema = z.object({ amount: z.string().regex(/^-?\d+(\.\d+)?$/), currency: z.lazy(() => CurrencySchema) });
export type Money = z.infer<typeof MoneySchema>;

export const ExpenseTypeSchema = z.lazy(() => SmallStrSchema);
export type ExpenseType = z.infer<typeof ExpenseTypeSchema>;

export const InvoiceIdSchema = z.string().brand<"InvoiceId">();
export type InvoiceId = z.infer<typeof InvoiceIdSchema>;

export const AttachmentIdSchema = z.string().brand<"AttachmentId">();
export type AttachmentId = z.infer<typeof AttachmentIdSchema>;

export const ImportIdSchema = z.string().brand<"ImportId">();
export type ImportId = z.infer<typeof ImportIdSchema>;

export const TransactionOriginSchema = z.discriminatedUnion("type", [z.object({ type: z.literal("WebForm") }), z.object({ type: z.literal("Import"), value: z.lazy(() => ImportIdSchema) })]);
export type TransactionOrigin = z.infer<typeof TransactionOriginSchema>;

export const AutoTxIdSchema = z.string().brand<"AutoTxId">();
export type AutoTxId = z.infer<typeof AutoTxIdSchema>;

export const AutoTransactionInfoDtoSchema = z.object({
	id: z.lazy(() => AutoTxIdSchema),
	name: z.string(),
	broken: z.boolean().optional(),
	invoice: z.lazy(() => InvoiceIdSchema).nullable().optional(),
});
export type AutoTransactionInfoDto = z.infer<typeof AutoTransactionInfoDtoSchema>;

export const TransactionDtoSchema = z.object({
	id: z.lazy(() => TransactionIdSchema),
	dateAdded: z.lazy(() => DateTimeStringSchema),
	datePaid: z.lazy(() => DateStringSchema),
	paymentDate: z.lazy(() => DateStringSchema),
	source: z.lazy(() => SourceSchema),
	merchant: z.string(),
	description: z.string(),
	group: z.lazy(() => GroupSchema),
	categories: z.array(z.lazy(() => CategorySchema)).optional(),
	foreignAmount: z.lazy(() => MoneySchema).nullable(),
	amount: z.lazy(() => MoneySchema),
	expenseType: z.lazy(() => ExpenseTypeSchema),
	invoice: z.lazy(() => InvoiceIdSchema).nullable(),
	receiptId: z.lazy(() => AttachmentIdSchema).nullable(),
	attachments: z.array(z.lazy(() => AttachmentIdSchema)).optional(),
	kid: z.string().nullable().optional(),
	payToAccount: z.string().nullable().optional(),
	reference: z.string().nullable().optional(),
	lastUpdated: z.lazy(() => DateTimeStringSchema),
	isIgnored: z.boolean(),
	revision: z.number().int(),
	origin: z.lazy(() => TransactionOriginSchema),
	appliedRules: z.array(z.lazy(() => AutoTransactionInfoDtoSchema)),
	matchingRules: z.array(z.lazy(() => AutoTransactionInfoDtoSchema)),
});
export type TransactionDto = z.infer<typeof TransactionDtoSchema>;

export const NewTransactionDtoSchema = z.object({
	datePaid: z.lazy(() => DateStringSchema),
	source: z.lazy(() => SourceSchema),
	merchant: z.string(),
	description: z.string(),
	group: z.lazy(() => GroupSchema),
	categories: z.array(z.lazy(() => CategorySchema)).optional(),
	foreignAmount: z.lazy(() => MoneySchema).nullable(),
	amount: z.lazy(() => MoneySchema),
	expenseType: z.lazy(() => ExpenseTypeSchema),
	invoice: z.lazy(() => InvoiceIdSchema).nullable(),
	receiptId: z.lazy(() => AttachmentIdSchema).nullable(),
});
export type NewTransactionDto = z.infer<typeof NewTransactionDtoSchema>;

export const ImportConfigDtoSchema = z.object({
	name: z.string(),
	format: z.string(),
	hasInvoice: z.boolean(),
	source: z.string().nullable(),
	invoiceParser: z.string().nullable(),
});
export type ImportConfigDto = z.infer<typeof ImportConfigDtoSchema>;

export const SortSchema = z.enum(["dateDesc", "dateAsc", "amountDesc", "amountAsc"]);
export type Sort = z.infer<typeof SortSchema>;

export const PredefinedFilterDtoSchema = z.object({
	name: z.string(),
	sort: z.lazy(() => SortSchema).nullable(),
	limit: z.number().int().nullable(),
}).extend(z.object({
	startDate: z.lazy(() => DateStringSchema).nullable().optional(),
	endDate: z.lazy(() => DateStringSchema).nullable().optional(),
	source: z.array(z.string()).nullable().optional(),
	group: z.array(z.string()).nullable().optional(),
	category: z.array(z.string()).nullable().optional(),
	expenseType: z.array(z.string()).nullable().optional(),
	merchant: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	autoTransactionId: z.array(z.lazy(() => AutoTxIdSchema)).nullable().optional(),
	invoice: z.array(z.lazy(() => InvoiceIdSchema)).nullable().optional(),
	importId: z.lazy(() => ImportIdSchema).nullable().optional(),
	ignored: z.boolean().nullable().optional(),
	matchProblem: z.boolean().nullable().optional(),
}).shape);
export type PredefinedFilterDto = z.infer<typeof PredefinedFilterDtoSchema>;

export const CurrencyFormatSchema = z.enum(["Nok"]);
export type CurrencyFormat = z.infer<typeof CurrencyFormatSchema>;

export const TransactionDefaultsDtoSchema = z.object({
	source: z.lazy(() => SourceSchema).nullable(),
	group: z.lazy(() => GroupSchema).nullable(),
	expenseType: z.lazy(() => ExpenseTypeSchema).nullable(),
});
export type TransactionDefaultsDto = z.infer<typeof TransactionDefaultsDtoSchema>;

export const ConfigDtoSchema = z.object({
	importConfigs: z.array(z.lazy(() => ImportConfigDtoSchema)),
	predefinedFilters: z.array(z.lazy(() => PredefinedFilterDtoSchema)),
	currencyFormat: z.lazy(() => CurrencyFormatSchema),
	manualTransactionDefaults: z.lazy(() => TransactionDefaultsDtoSchema),
});
export type ConfigDto = z.infer<typeof ConfigDtoSchema>;

export const GetTransactionsQueryDtoSchema = z.object({
	limit: z.number().int().nullable().optional(),
	cursor: z.string().nullable().optional(),
	sort: z.lazy(() => SortSchema).nullable().optional(),
}).extend(z.object({
	startDate: z.lazy(() => DateStringSchema).nullable().optional(),
	endDate: z.lazy(() => DateStringSchema).nullable().optional(),
	source: z.array(z.string()).nullable().optional(),
	group: z.array(z.string()).nullable().optional(),
	category: z.array(z.string()).nullable().optional(),
	expenseType: z.array(z.string()).nullable().optional(),
	merchant: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	autoTransactionId: z.array(z.lazy(() => AutoTxIdSchema)).nullable().optional(),
	invoice: z.array(z.lazy(() => InvoiceIdSchema)).nullable().optional(),
	importId: z.lazy(() => ImportIdSchema).nullable().optional(),
	ignored: z.boolean().nullable().optional(),
	matchProblem: z.boolean().nullable().optional(),
}).shape);
export type GetTransactionsQueryDto = z.infer<typeof GetTransactionsQueryDtoSchema>;

export const SourceIdSchema = z.string().brand<"SourceId">();
export type SourceId = z.infer<typeof SourceIdSchema>;

export const GroupIdSchema = z.string().brand<"GroupId">();
export type GroupId = z.infer<typeof GroupIdSchema>;

export const ExpenseTypeIdSchema = z.string().brand<"ExpenseTypeId">();
export type ExpenseTypeId = z.infer<typeof ExpenseTypeIdSchema>;

export const CategoryIdSchema = z.string().brand<"CategoryId">();
export type CategoryId = z.infer<typeof CategoryIdSchema>;

export const InvoiceSchema = z.lazy(() => SmallStrSchema);
export type Invoice = z.infer<typeof InvoiceSchema>;

export const InvoiceRuleAppliedSchema = z.object({
	invoice: z.lazy(() => InvoiceIdSchema),
	name: z.lazy(() => InvoiceSchema),
});
export type InvoiceRuleApplied = z.infer<typeof InvoiceRuleAppliedSchema>;

export const ActionSetIdSchema = z.string().brand<"ActionSetId">();
export type ActionSetId = z.infer<typeof ActionSetIdSchema>;

export const ActionSetInputValueSchema = z.object({
	name: z.string(),
	value: z.string(),
});
export type ActionSetInputValue = z.infer<typeof ActionSetInputValueSchema>;

export const ActionSetRefSchema = z.object({
	set: z.lazy(() => ActionSetIdSchema),
	inputs: z.array(z.lazy(() => ActionSetInputValueSchema)),
});
export type ActionSetRef = z.infer<typeof ActionSetRefSchema>;

export const RetiredFieldSchema = z.enum(["account"]);
export type RetiredField = z.infer<typeof RetiredFieldSchema>;

export const ConditionalActionMatcherDtoSchema = z.discriminatedUnion("field", [z.object({ field: z.literal("source"), value: z.array(z.lazy(() => SourceIdSchema)) }), z.object({ field: z.literal("group"), value: z.array(z.lazy(() => GroupIdSchema)) }), z.object({ field: z.literal("expenseType"), value: z.array(z.lazy(() => ExpenseTypeIdSchema)) }), z.object({ field: z.literal("category"), value: z.array(z.lazy(() => CategoryIdSchema)) }), z.object({ field: z.literal("retired"), value: z.lazy(() => RetiredFieldSchema) })]);
export type ConditionalActionMatcherDto = z.infer<typeof ConditionalActionMatcherDtoSchema>;

export const ConditionalActionDtoSchema = z.object({
	condition: z.lazy(() => ConditionalActionMatcherDtoSchema),
	action: z.lazy(() => EditActionDtoSchema),
});
export type ConditionalActionDto = z.infer<typeof ConditionalActionDtoSchema>;

export const MatchActionDtoSchema = z.object({
	arms: z.array(z.lazy(() => ConditionalActionDtoSchema)),
});
export type MatchActionDto = z.infer<typeof MatchActionDtoSchema>;

export const FieldNameSchema = z.enum(["source", "merchant", "description", "group", "amount", "expenseType", "datePaid", "kid", "payToAccount"]);
export type FieldName = z.infer<typeof FieldNameSchema>;

export const PromptSpecSchema = z.object({
	field: z.lazy(() => FieldNameSchema),
	prefix: z.string().nullable(),
});
export type PromptSpec = z.infer<typeof PromptSpecSchema>;

export const TextFieldNameSchema = z.enum(["merchant", "description"]);
export type TextFieldName = z.infer<typeof TextFieldNameSchema>;

export const CopyFieldSpecSchema = z.object({
	from: z.lazy(() => FieldNameSchema),
	to: z.lazy(() => TextFieldNameSchema),
});
export type CopyFieldSpec = z.infer<typeof CopyFieldSpecSchema>;

export type EditActionDto = { field: "datePaid"; value: DateString } | { field: "source"; value: SourceId } | { field: "merchant"; value: string } | { field: "description"; value: string } | { field: "group"; value: GroupId } | { field: "foreignAmount"; value: (Money) | null } | { field: "amount"; value: Money } | { field: "expenseType"; value: ExpenseTypeId } | { field: "split"; value: Money } | { field: "addCategory"; value: Array<CategoryId> } | { field: "removeCategory"; value: Array<CategoryId> } | { field: "addAttachment"; value: AttachmentId } | { field: "removeAttachment"; value: AttachmentId } | { field: "receipt"; value: (AttachmentId) | null } | { field: "invoice"; value: (InvoiceId) | null } | { field: "applyAutoTx"; value: AutoTxId } | { field: "invoiceRuleApplied"; value: InvoiceRuleApplied } | { field: "applyActionSet"; value: ActionSetRef } | { field: "conditional"; value: ConditionalActionDto } | { field: "match"; value: MatchActionDto } | { field: "ignore"; value: boolean } | { field: "prompt"; value: PromptSpec } | { field: "copyField"; value: CopyFieldSpec } | { field: "retired"; value: RetiredField };
export const EditActionDtoSchema: z.ZodType<EditActionDto> = z.discriminatedUnion("field", [z.object({ field: z.literal("datePaid"), value: z.lazy(() => DateStringSchema) }), z.object({ field: z.literal("source"), value: z.lazy(() => SourceIdSchema) }), z.object({ field: z.literal("merchant"), value: z.string() }), z.object({ field: z.literal("description"), value: z.string() }), z.object({ field: z.literal("group"), value: z.lazy(() => GroupIdSchema) }), z.object({ field: z.literal("foreignAmount"), value: z.lazy(() => MoneySchema).nullable() }), z.object({ field: z.literal("amount"), value: z.lazy(() => MoneySchema) }), z.object({ field: z.literal("expenseType"), value: z.lazy(() => ExpenseTypeIdSchema) }), z.object({ field: z.literal("split"), value: z.lazy(() => MoneySchema) }), z.object({ field: z.literal("addCategory"), value: z.array(z.lazy(() => CategoryIdSchema)) }), z.object({ field: z.literal("removeCategory"), value: z.array(z.lazy(() => CategoryIdSchema)) }), z.object({ field: z.literal("addAttachment"), value: z.lazy(() => AttachmentIdSchema) }), z.object({ field: z.literal("removeAttachment"), value: z.lazy(() => AttachmentIdSchema) }), z.object({ field: z.literal("receipt"), value: z.lazy(() => AttachmentIdSchema).nullable() }), z.object({ field: z.literal("invoice"), value: z.lazy(() => InvoiceIdSchema).nullable() }), z.object({ field: z.literal("applyAutoTx"), value: z.lazy(() => AutoTxIdSchema) }), z.object({ field: z.literal("invoiceRuleApplied"), value: z.lazy(() => InvoiceRuleAppliedSchema) }), z.object({ field: z.literal("applyActionSet"), value: z.lazy(() => ActionSetRefSchema) }), z.object({ field: z.literal("conditional"), value: z.lazy(() => ConditionalActionDtoSchema) }), z.object({ field: z.literal("match"), value: z.lazy(() => MatchActionDtoSchema) }), z.object({ field: z.literal("ignore"), value: z.boolean() }), z.object({ field: z.literal("prompt"), value: z.lazy(() => PromptSpecSchema) }), z.object({ field: z.literal("copyField"), value: z.lazy(() => CopyFieldSpecSchema) }), z.object({ field: z.literal("retired"), value: z.lazy(() => RetiredFieldSchema) })]);

export const TransactionUpdateDtoSchema = z.object({
	id: z.lazy(() => TransactionIdSchema),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
});
export type TransactionUpdateDto = z.infer<typeof TransactionUpdateDtoSchema>;

export const PaginationDtoSchema = z.object({
	limit: z.number().int(),
	cursor: z.string().nullable(),
	sort: z.lazy(() => SortSchema).nullable(),
});
export type PaginationDto = z.infer<typeof PaginationDtoSchema>;

export const EditDtoSchema = z.object({
	date: z.lazy(() => DateTimeStringSchema),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
});
export type EditDto = z.infer<typeof EditDtoSchema>;

export const SummaryItemDtoSchema = z.object({
	id: z.string(),
	income: z.lazy(() => MoneySchema),
	expense: z.lazy(() => MoneySchema),
	total: z.lazy(() => MoneySchema),
});
export type SummaryItemDto = z.infer<typeof SummaryItemDtoSchema>;

export const MonthlySummaryDtoSchema = z.object({
	groups: z.array(z.lazy(() => SummaryItemDtoSchema)),
	accounts: z.array(z.lazy(() => SummaryItemDtoSchema)),
	categories: z.array(z.lazy(() => SummaryItemDtoSchema)),
	totalIncome: z.lazy(() => MoneySchema),
	totalExpense: z.lazy(() => MoneySchema),
	totalNet: z.lazy(() => MoneySchema),
});
export type MonthlySummaryDto = z.infer<typeof MonthlySummaryDtoSchema>;

export const GetMonthlySummaryQueryDtoSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("month"),
	year: z.number().int(),
	month: z.number().int(),
	source: z.string().nullable(),
}), z.object({
	type: z.literal("invoice"),
	invoiceId: z.lazy(() => InvoiceIdSchema),
})]);
export type GetMonthlySummaryQueryDto = z.infer<typeof GetMonthlySummaryQueryDtoSchema>;

export const MonthlyPendingBucketDtoSchema = z.object({
	year: z.number().int(),
	month: z.number().int(),
	count: z.number().int(),
});
export type MonthlyPendingBucketDto = z.infer<typeof MonthlyPendingBucketDtoSchema>;

export const MonthlyPendingSummaryDtoSchema = z.object({
	buckets: z.array(z.lazy(() => MonthlyPendingBucketDtoSchema)),
});
export type MonthlyPendingSummaryDto = z.infer<typeof MonthlyPendingSummaryDtoSchema>;

export const GetMonthSummaryQueryDtoSchema = z.object({
	limit: z.number().int().nullable().optional(),
}).extend(z.object({
	startDate: z.lazy(() => DateStringSchema).nullable().optional(),
	endDate: z.lazy(() => DateStringSchema).nullable().optional(),
	source: z.array(z.string()).nullable().optional(),
	group: z.array(z.string()).nullable().optional(),
	category: z.array(z.string()).nullable().optional(),
	expenseType: z.array(z.string()).nullable().optional(),
	merchant: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	autoTransactionId: z.array(z.lazy(() => AutoTxIdSchema)).nullable().optional(),
	invoice: z.array(z.lazy(() => InvoiceIdSchema)).nullable().optional(),
	importId: z.lazy(() => ImportIdSchema).nullable().optional(),
	ignored: z.boolean().nullable().optional(),
	matchProblem: z.boolean().nullable().optional(),
}).shape);
export type GetMonthSummaryQueryDto = z.infer<typeof GetMonthSummaryQueryDtoSchema>;

export const FieldChangeDtoSchema = z.object({
	field: z.string(),
	oldValue: z.string().nullable(),
	newValue: z.string().nullable(),
});
export type FieldChangeDto = z.infer<typeof FieldChangeDtoSchema>;

export const HistoryEntryDtoSchema = z.object({
	date: z.lazy(() => DateTimeStringSchema),
	description: z.string(),
	changes: z.array(z.lazy(() => FieldChangeDtoSchema)),
	error: z.string().nullable().optional(),
});
export type HistoryEntryDto = z.infer<typeof HistoryEntryDtoSchema>;

export const MatchProblemDtoSchema = z.object({
	rule: z.lazy(() => AutoTransactionInfoDtoSchema).nullable(),
	field: z.lazy(() => FieldNameSchema),
	value: z.string(),
});
export type MatchProblemDto = z.infer<typeof MatchProblemDtoSchema>;

export const TransactionDetailsDtoSchema = z.object({
	history: z.array(z.lazy(() => HistoryEntryDtoSchema)),
	problems: z.array(z.lazy(() => MatchProblemDtoSchema)),
}).extend(z.object({
	id: z.lazy(() => TransactionIdSchema),
	dateAdded: z.lazy(() => DateTimeStringSchema),
	datePaid: z.lazy(() => DateStringSchema),
	paymentDate: z.lazy(() => DateStringSchema),
	source: z.lazy(() => SourceSchema),
	merchant: z.string(),
	description: z.string(),
	group: z.lazy(() => GroupSchema),
	categories: z.array(z.lazy(() => CategorySchema)).optional(),
	foreignAmount: z.lazy(() => MoneySchema).nullable(),
	amount: z.lazy(() => MoneySchema),
	expenseType: z.lazy(() => ExpenseTypeSchema),
	invoice: z.lazy(() => InvoiceIdSchema).nullable(),
	receiptId: z.lazy(() => AttachmentIdSchema).nullable(),
	attachments: z.array(z.lazy(() => AttachmentIdSchema)).optional(),
	kid: z.string().nullable().optional(),
	payToAccount: z.string().nullable().optional(),
	reference: z.string().nullable().optional(),
	lastUpdated: z.lazy(() => DateTimeStringSchema),
	isIgnored: z.boolean(),
	revision: z.number().int(),
	origin: z.lazy(() => TransactionOriginSchema),
	appliedRules: z.array(z.lazy(() => AutoTransactionInfoDtoSchema)),
	matchingRules: z.array(z.lazy(() => AutoTransactionInfoDtoSchema)),
}).shape);
export type TransactionDetailsDto = z.infer<typeof TransactionDetailsDtoSchema>;

export const PreviewActionsDtoSchema = z.object({
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
});
export type PreviewActionsDto = z.infer<typeof PreviewActionsDtoSchema>;

export const TransactionPreviewDtoSchema = z.object({
	details: z.lazy(() => TransactionDtoSchema),
	changes: z.array(z.lazy(() => FieldChangeDtoSchema)),
	problems: z.array(z.lazy(() => MatchProblemDtoSchema)),
});
export type TransactionPreviewDto = z.infer<typeof TransactionPreviewDtoSchema>;

export const PayeeIdSchema = z.string().brand<"PayeeId">();
export type PayeeId = z.infer<typeof PayeeIdSchema>;

export const DetectorTransactionInfoDtoSchema = z.object({
	expenseType: z.string(),
	group: z.string(),
	categories: z.array(z.string()),
});
export type DetectorTransactionInfoDto = z.infer<typeof DetectorTransactionInfoDtoSchema>;

export const InvoiceMatchSnapshotSchema = z.object({
	detectorId: z.string(),
	transactionInfo: z.lazy(() => DetectorTransactionInfoDtoSchema),
});
export type InvoiceMatchSnapshot = z.infer<typeof InvoiceMatchSnapshotSchema>;

export const InvoiceEntitySchema = z.object({
	id: z.lazy(() => InvoiceIdSchema),
	name: z.lazy(() => InvoiceSchema),
	description: z.string().nullable(),
	sourceId: z.lazy(() => SourceIdSchema).nullable().optional(),
	payeeId: z.lazy(() => PayeeIdSchema).nullable().optional(),
	dateReceived: z.lazy(() => DateStringSchema),
	dueDate: z.lazy(() => DateStringSchema),
	amount: z.lazy(() => MoneySchema).nullable(),
	amountMax: z.lazy(() => MoneySchema).nullable().optional(),
	kid: z.string().nullable(),
	payToAccount: z.string().nullable(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	attachments: z.array(z.lazy(() => AttachmentIdSchema)).optional(),
	matchSnapshot: z.lazy(() => InvoiceMatchSnapshotSchema).nullable().optional(),
});
export type InvoiceEntity = z.infer<typeof InvoiceEntitySchema>;

export const NewInvoiceEntitySchema = z.object({
	name: z.lazy(() => InvoiceSchema),
	description: z.string().nullable(),
	sourceId: z.lazy(() => SourceIdSchema).nullable().optional(),
	payeeId: z.lazy(() => PayeeIdSchema).nullable().optional(),
	dateReceived: z.lazy(() => DateStringSchema),
	dueDate: z.lazy(() => DateStringSchema),
	amount: z.lazy(() => MoneySchema).nullable(),
	amountMax: z.lazy(() => MoneySchema).nullable().optional(),
	kid: z.string().nullable(),
	payToAccount: z.string().nullable(),
	attachments: z.array(z.lazy(() => AttachmentIdSchema)).optional(),
	matchSnapshot: z.lazy(() => InvoiceMatchSnapshotSchema).nullable().optional(),
});
export type NewInvoiceEntity = z.infer<typeof NewInvoiceEntitySchema>;

export const UpdateInvoiceEntitySchema = z.object({
	id: z.lazy(() => InvoiceIdSchema),
	name: z.lazy(() => InvoiceSchema),
	description: z.string().nullable(),
	sourceId: z.lazy(() => SourceIdSchema).nullable().optional(),
	payeeId: z.lazy(() => PayeeIdSchema).nullable().optional(),
	dateReceived: z.lazy(() => DateStringSchema),
	dueDate: z.lazy(() => DateStringSchema),
	amount: z.lazy(() => MoneySchema).nullable(),
	amountMax: z.lazy(() => MoneySchema).nullable().optional(),
	kid: z.string().nullable(),
	payToAccount: z.string().nullable(),
	attachments: z.array(z.lazy(() => AttachmentIdSchema)).optional(),
});
export type UpdateInvoiceEntity = z.infer<typeof UpdateInvoiceEntitySchema>;

export const DuplicateKeyFieldSchema = z.enum(["kid", "dueDate", "dateReceived", "amount", "name"]);
export type DuplicateKeyField = z.infer<typeof DuplicateKeyFieldSchema>;

export const PeriodGranularitySchema = z.enum(["month", "quarter", "year"]);
export type PeriodGranularity = z.infer<typeof PeriodGranularitySchema>;

export const DetectorTransformSchema = z.enum(["norwegianDate", "norwegianAmount", "plainAmount", "spacesToDots", "norwegianAccount", "norwegianCompactDate", "isoDate", "norwegianTextDate", "trim"]);
export type DetectorTransform = z.infer<typeof DetectorTransformSchema>;

export const DetectorFieldRuleDtoSchema = z.object({
	pattern: z.string().nullable().optional(),
	group: z.number().int().nullable().optional(),
	transform: z.lazy(() => DetectorTransformSchema).nullable().optional(),
	value: z.string().nullable().optional(),
	offsetDays: z.number().int().nullable().optional(),
	dayOfMonth: z.number().int().nullable().optional(),
});
export type DetectorFieldRuleDto = z.infer<typeof DetectorFieldRuleDtoSchema>;

export const DetectorFieldsDtoSchema = z.object({
	dateReceived: z.lazy(() => DetectorFieldRuleDtoSchema),
	dueDate: z.lazy(() => DetectorFieldRuleDtoSchema),
	payToAccount: z.lazy(() => DetectorFieldRuleDtoSchema).nullable().optional(),
	kid: z.lazy(() => DetectorFieldRuleDtoSchema).nullable().optional(),
	period: z.lazy(() => DetectorFieldRuleDtoSchema).nullable().optional(),
});
export type DetectorFieldsDto = z.infer<typeof DetectorFieldsDtoSchema>;

export const DetectorAmountRuleDtoSchema = z.object({
	pattern: z.string().nullable().optional(),
	amountGroup: z.number().int().nullable().optional(),
	value: z.string().nullable().optional(),
	currencyGroup: z.number().int().nullable().optional(),
	currency: z.lazy(() => CurrencySchema).nullable().optional(),
	transform: z.lazy(() => DetectorTransformSchema).nullable().optional(),
});
export type DetectorAmountRuleDto = z.infer<typeof DetectorAmountRuleDtoSchema>;

export const DetectorExtraPaymentDtoSchema = z.object({
	min: z.number(),
	max: z.number(),
	withinDays: z.number().int(),
});
export type DetectorExtraPaymentDto = z.infer<typeof DetectorExtraPaymentDtoSchema>;

export const UnreadableVariantDtoSchema = z.object({
	id: z.string(),
	mustContain: z.array(z.string()),
	reason: z.string(),
});
export type UnreadableVariantDto = z.infer<typeof UnreadableVariantDtoSchema>;

export const InvoiceDetectorDtoSchema = z.object({
	id: z.string(),
	label: z.string(),
	mustContain: z.array(z.string()),
	source: z.string().nullable().optional(),
	payee: z.string().nullable().optional(),
	nameTemplate: z.string(),
	filenameTemplate: z.string(),
	duplicateKey: z.array(z.lazy(() => DuplicateKeyFieldSchema)),
	periodGranularity: z.lazy(() => PeriodGranularitySchema).optional(),
	transactionInfo: z.lazy(() => DetectorTransactionInfoDtoSchema).nullable().optional(),
	fields: z.lazy(() => DetectorFieldsDtoSchema),
	amount: z.lazy(() => DetectorAmountRuleDtoSchema).nullable().optional(),
	amountMax: z.lazy(() => DetectorAmountRuleDtoSchema).nullable().optional(),
	extraPayment: z.array(z.lazy(() => DetectorExtraPaymentDtoSchema)).optional(),
	unreadable: z.array(z.lazy(() => UnreadableVariantDtoSchema)).optional(),
});
export type InvoiceDetectorDto = z.infer<typeof InvoiceDetectorDtoSchema>;

export const UnsupportedInvoiceDtoSchema = z.object({
	id: z.string(),
	label: z.string(),
	mustContain: z.array(z.string()),
	reason: z.string(),
});
export type UnsupportedInvoiceDto = z.infer<typeof UnsupportedInvoiceDtoSchema>;

export const InvoiceDetectorsDtoSchema = z.object({
	detectors: z.array(z.lazy(() => InvoiceDetectorDtoSchema)),
	unsupported: z.array(z.lazy(() => UnsupportedInvoiceDtoSchema)),
});
export type InvoiceDetectorsDto = z.infer<typeof InvoiceDetectorsDtoSchema>;

export const ImportValidationIssueSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("sourceNotFound"),
	source: z.lazy(() => SourceSchema),
}), z.object({
	type: z.literal("groupNotFound"),
	group: z.lazy(() => GroupSchema),
})]);
export type ImportValidationIssue = z.infer<typeof ImportValidationIssueSchema>;

export const MatchSetIdSchema = z.string().brand<"MatchSetId">();
export type MatchSetId = z.infer<typeof MatchSetIdSchema>;

export const MatchSetEntryRefDtoSchema = z.object({
	id: z.lazy(() => MatchSetIdSchema),
	name: z.string(),
});
export type MatchSetEntryRefDto = z.infer<typeof MatchSetEntryRefDtoSchema>;

export const ActionSetEntryRefDtoSchema = z.object({
	id: z.lazy(() => ActionSetIdSchema),
	name: z.string(),
});
export type ActionSetEntryRefDto = z.infer<typeof ActionSetEntryRefDtoSchema>;

export const LibraryUsageDtoSchema = z.object({
	rules: z.array(z.lazy(() => AutoTransactionInfoDtoSchema)),
	matchSets: z.array(z.lazy(() => MatchSetEntryRefDtoSchema)),
	actionSets: z.array(z.lazy(() => ActionSetEntryRefDtoSchema)),
});
export type LibraryUsageDto = z.infer<typeof LibraryUsageDtoSchema>;

export const StoredTxIdSchema = z.string().brand<"StoredTxId">();
export type StoredTxId = z.infer<typeof StoredTxIdSchema>;

export const ImportItemSchema = z.object({
	id: z.lazy(() => ImportIdSchema),
}).extend(z.object({
	importerName: z.string(),
	dateImported: z.lazy(() => DateTimeStringSchema),
	invoiceId: z.lazy(() => InvoiceIdSchema).nullable().optional(),
	firstTransactionDate: z.lazy(() => DateStringSchema).nullable().optional(),
	lastTransactionDate: z.lazy(() => DateStringSchema).nullable().optional(),
	pendingDiffCount: z.number().int().optional(),
	latestPendingDiffAt: z.lazy(() => DateTimeStringSchema).nullable().optional(),
	deletedAt: z.lazy(() => DateTimeStringSchema).nullable().optional(),
	finalizedAt: z.lazy(() => DateTimeStringSchema).nullable().optional(),
	importConfigVersion: z.number().int().optional(),
	needsRerun: z.boolean().optional(),
}).shape);
export type ImportItem = z.infer<typeof ImportItemSchema>;

export const PendingIdSchema = z.string().brand<"PendingId">();
export type PendingId = z.infer<typeof PendingIdSchema>;

export const AppErrorDtoSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("generic"),
	error: z.lazy(() => SmallStrSchema),
}), z.object({
	type: z.literal("importValidation"),
	error: z.lazy(() => SmallStrSchema),
	issues: z.array(z.lazy(() => ImportValidationIssueSchema)),
}), z.object({
	type: z.literal("entityNotFound"),
	error: z.lazy(() => SmallStrSchema),
	entityType: z.lazy(() => SmallStrSchema),
	value: z.lazy(() => SmallStrSchema),
}), z.object({
	type: z.literal("missingImportField"),
	error: z.lazy(() => SmallStrSchema),
	field: z.lazy(() => SmallStrSchema),
}), z.object({
	type: z.literal("unmappedImportValue"),
	error: z.lazy(() => SmallStrSchema),
	field: z.lazy(() => SmallStrSchema),
	value: z.lazy(() => SmallStrSchema),
}), z.object({
	type: z.literal("libraryEntryInUse"),
	error: z.lazy(() => SmallStrSchema),
	usage: z.lazy(() => LibraryUsageDtoSchema),
}), z.object({
	type: z.literal("autoTransactionInUse"),
	error: z.lazy(() => SmallStrSchema),
	ruleId: z.lazy(() => AutoTxIdSchema),
	transactionIds: z.array(z.lazy(() => StoredTxIdSchema)),
}), z.object({
	type: z.literal("importConflict"),
	error: z.lazy(() => SmallStrSchema),
	firstTransactionDate: z.lazy(() => DateStringSchema),
	lastTransactionDate: z.lazy(() => DateStringSchema),
	conflictingImports: z.array(z.lazy(() => ImportItemSchema)),
}), z.object({
	type: z.literal("invoiceDuplicate"),
	error: z.lazy(() => SmallStrSchema),
	existingInvoice: z.lazy(() => InvoiceEntitySchema),
}), z.object({
	type: z.literal("pendingImportFileExpired"),
	error: z.lazy(() => SmallStrSchema),
	importId: z.lazy(() => ImportIdSchema),
	pendingId: z.lazy(() => PendingIdSchema),
}), z.object({
	type: z.literal("pendingImportDiffStale"),
	error: z.lazy(() => SmallStrSchema),
	importId: z.lazy(() => ImportIdSchema),
	pendingId: z.lazy(() => PendingIdSchema),
})]);
export type AppErrorDto = z.infer<typeof AppErrorDtoSchema>;

export const VersionInfoDtoSchema = z.object({
	gitRef: z.string(),
	gitCommit: z.string(),
	buildDate: z.string(),
});
export type VersionInfoDto = z.infer<typeof VersionInfoDtoSchema>;

export const ImportSortSchema = z.enum(["uploadDateDesc", "firstTxDateDesc"]);
export type ImportSort = z.infer<typeof ImportSortSchema>;

export const GetImportsQueryDtoSchema = z.object({
	sort: z.lazy(() => ImportSortSchema).nullable().optional(),
});
export type GetImportsQueryDto = z.infer<typeof GetImportsQueryDtoSchema>;

export const ImportResultDtoSchema = z.object({
	imported: z.number().int(),
	skippedFinalized: z.number().int(),
	mergedTransfers: z.number().int(),
});
export type ImportResultDto = z.infer<typeof ImportResultDtoSchema>;

export const UpdateImportDtoSchema = z.object({
	invoiceId: z.lazy(() => InvoiceIdSchema).nullable(),
	deleted: z.boolean().nullable(),
	finalized: z.boolean().nullable().optional(),
});
export type UpdateImportDto = z.infer<typeof UpdateImportDtoSchema>;

export const ImportDiffTransactionDtoSchema = z.object({
	id: z.lazy(() => StoredTxIdSchema).nullable(),
	datePaid: z.lazy(() => DateStringSchema),
	merchant: z.string(),
	description: z.string(),
	source: z.lazy(() => SourceSchema),
	group: z.lazy(() => GroupSchema),
	amount: z.lazy(() => MoneySchema),
	foreignAmount: z.lazy(() => MoneySchema).nullable(),
	isReserved: z.boolean(),
});
export type ImportDiffTransactionDto = z.infer<typeof ImportDiffTransactionDtoSchema>;

export const SettlingTransactionDtoSchema = z.object({
	existingId: z.lazy(() => StoredTxIdSchema),
	reserved: z.lazy(() => ImportDiffTransactionDtoSchema),
	settled: z.lazy(() => ImportDiffTransactionDtoSchema),
});
export type SettlingTransactionDto = z.infer<typeof SettlingTransactionDtoSchema>;

export const UpdatedTransactionDtoSchema = z.object({
	id: z.lazy(() => StoredTxIdSchema),
	datePaid: z.lazy(() => DateStringSchema),
	merchant: z.string(),
	amount: z.lazy(() => MoneySchema),
	oldKid: z.string().nullable(),
	newKid: z.string().nullable(),
	oldPayToAccount: z.string().nullable(),
	newPayToAccount: z.string().nullable(),
	oldReference: z.string().nullable().optional(),
	newReference: z.string().nullable().optional(),
	oldDescription: z.string().nullable().optional(),
	newDescription: z.string().nullable().optional(),
});
export type UpdatedTransactionDto = z.infer<typeof UpdatedTransactionDtoSchema>;

export const RestatedTransactionDtoSchema = z.object({
	existingId: z.lazy(() => StoredTxIdSchema),
	stored: z.lazy(() => ImportDiffTransactionDtoSchema),
	restated: z.lazy(() => ImportDiffTransactionDtoSchema),
});
export type RestatedTransactionDto = z.infer<typeof RestatedTransactionDtoSchema>;

export const ImportDiffDtoSchema = z.object({
	pendingId: z.lazy(() => PendingIdSchema),
	added: z.array(z.lazy(() => ImportDiffTransactionDtoSchema)),
	removed: z.array(z.lazy(() => ImportDiffTransactionDtoSchema)),
	merged: z.array(z.lazy(() => ImportDiffTransactionDtoSchema)).optional(),
	unchangedCount: z.number().int(),
	settling: z.array(z.lazy(() => SettlingTransactionDtoSchema)),
	updated: z.array(z.lazy(() => UpdatedTransactionDtoSchema)).optional(),
	restated: z.array(z.lazy(() => RestatedTransactionDtoSchema)).optional(),
});
export type ImportDiffDto = z.infer<typeof ImportDiffDtoSchema>;

export const PendingImportFileDtoSchema = z.object({
	pendingId: z.lazy(() => PendingIdSchema),
	createdAt: z.lazy(() => DateTimeStringSchema),
});
export type PendingImportFileDto = z.infer<typeof PendingImportFileDtoSchema>;

export const LogoKindSchema = z.enum(["source", "group", "category", "expense-type", "payee"]);
export type LogoKind = z.infer<typeof LogoKindSchema>;

export const EntityStyleSchema = z.enum(["normal", "bold", "italic"]);
export type EntityStyle = z.infer<typeof EntityStyleSchema>;

export const AccountKindSchema = z.enum(["checking", "savings", "investment", "creditCard", "loan"]);
export type AccountKind = z.infer<typeof AccountKindSchema>;

export const PayeeSchema = z.lazy(() => SmallStrSchema);
export type Payee = z.infer<typeof PayeeSchema>;

export const EntityRegistrySchema = z.object({
	sources: z.record(z.lazy(() => SmallStrSchema), z.object({
	id: z.lazy(() => SourceIdSchema),
	name: z.lazy(() => SourceSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
})),
	groups: z.record(z.lazy(() => SmallStrSchema), z.object({
	id: z.lazy(() => GroupIdSchema),
	name: z.lazy(() => GroupSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
})),
	categories: z.record(z.lazy(() => SmallStrSchema), z.object({
	id: z.lazy(() => CategoryIdSchema),
	name: z.lazy(() => CategorySchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
})),
	expenseTypes: z.record(z.lazy(() => SmallStrSchema), z.object({
	id: z.lazy(() => ExpenseTypeIdSchema),
	name: z.lazy(() => ExpenseTypeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
})),
	payees: z.record(z.lazy(() => SmallStrSchema), z.object({
	id: z.lazy(() => PayeeIdSchema),
	name: z.lazy(() => PayeeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
})),
	invoices: z.record(z.lazy(() => InvoiceIdSchema), z.lazy(() => InvoiceEntitySchema)),
});
export type EntityRegistry = z.infer<typeof EntityRegistrySchema>;

export const OperatorSchema = z.enum(["equal", "contains", "regex", "is", "isOneOf", "greaterThan", "lessThan", "between", "onDay", "betweenDays", "dateBetween"]);
export type Operator = z.infer<typeof OperatorSchema>;

export type ConditionDto = { type: "all"; conditions: Array<ConditionDto> } | { type: "any"; conditions: Array<ConditionDto> } | { type: "field"; field: FieldName; operator: Operator; value: string } | { type: "ref"; set: MatchSetId } | { type: "retired"; field: RetiredField };
export const ConditionDtoSchema: z.ZodType<ConditionDto> = z.discriminatedUnion("type", [z.object({
	type: z.literal("all"),
	conditions: z.array(z.lazy(() => ConditionDtoSchema)),
}), z.object({
	type: z.literal("any"),
	conditions: z.array(z.lazy(() => ConditionDtoSchema)),
}), z.object({
	type: z.literal("field"),
	field: z.lazy(() => FieldNameSchema),
	operator: z.lazy(() => OperatorSchema),
	value: z.string(),
}), z.object({
	type: z.literal("ref"),
	set: z.lazy(() => MatchSetIdSchema),
}), z.object({
	type: z.literal("retired"),
	field: z.lazy(() => RetiredFieldSchema),
})]);

export const RuleProblemDtoSchema = z.discriminatedUnion("type", [z.object({
	type: z.literal("missingMatchSet"),
	id: z.lazy(() => MatchSetIdSchema),
}), z.object({
	type: z.literal("missingActionSet"),
	id: z.lazy(() => ActionSetIdSchema),
}), z.object({
	type: z.literal("hiddenEntity"),
	kind: z.string(),
	name: z.string(),
}), z.object({
	type: z.literal("missingEntity"),
	kind: z.string(),
	name: z.string(),
}), z.object({
	type: z.literal("conditionMatchesNoEntity"),
	kind: z.string(),
	value: z.string(),
}), z.object({
	type: z.literal("missingActionSetInput"),
	id: z.lazy(() => ActionSetIdSchema),
	name: z.string(),
}), z.object({
	type: z.literal("unknownTemplatePlaceholder"),
	name: z.string(),
}), z.object({
	type: z.literal("malformedTemplate"),
	text: z.string(),
}), z.object({
	type: z.literal("retiredField"),
	field: z.lazy(() => RetiredFieldSchema),
}), z.object({
	type: z.literal("unreadable"),
	error: z.string(),
})]);
export type RuleProblemDto = z.infer<typeof RuleProblemDtoSchema>;

export const AutoTransactionRuleDtoSchema = z.object({
	id: z.lazy(() => AutoTxIdSchema),
	name: z.string(),
	description: z.string().nullable(),
	priority: z.number().int(),
	condition: z.lazy(() => ConditionDtoSchema),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
	enabled: z.boolean(),
	editable: z.boolean(),
	problems: z.array(z.lazy(() => RuleProblemDtoSchema)),
});
export type AutoTransactionRuleDto = z.infer<typeof AutoTransactionRuleDtoSchema>;

export const NewAutoTransactionRuleDtoSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
	priority: z.number().int(),
	condition: z.lazy(() => ConditionDtoSchema),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
	enabled: z.boolean(),
});
export type NewAutoTransactionRuleDto = z.infer<typeof NewAutoTransactionRuleDtoSchema>;

export const UpdateAutoTransactionRuleDtoSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
	priority: z.number().int(),
	condition: z.lazy(() => ConditionDtoSchema),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
	enabled: z.boolean(),
});
export type UpdateAutoTransactionRuleDto = z.infer<typeof UpdateAutoTransactionRuleDtoSchema>;

export const PreviewMatchesRequestDtoSchema = z.object({
	condition: z.lazy(() => ConditionDtoSchema),
});
export type PreviewMatchesRequestDto = z.infer<typeof PreviewMatchesRequestDtoSchema>;

export const DerivedAccountSchema = z.enum(["savings", "creditCardFixed", "creditCardVariable"]);
export type DerivedAccount = z.infer<typeof DerivedAccountSchema>;

export const TransactionListDtoSchema = z.object({
	id: z.lazy(() => TransactionIdSchema),
	datePaid: z.lazy(() => DateStringSchema),
	paymentDate: z.lazy(() => DateStringSchema),
	source: z.lazy(() => SourceSchema),
	merchant: z.string(),
	description: z.string(),
	derivedAccount: z.lazy(() => DerivedAccountSchema).nullable(),
	group: z.lazy(() => GroupSchema),
	categories: z.array(z.lazy(() => CategorySchema)).optional(),
	foreignAmount: z.lazy(() => MoneySchema).nullable(),
	amount: z.lazy(() => MoneySchema),
	expenseType: z.lazy(() => ExpenseTypeSchema),
	invoice: z.lazy(() => InvoiceIdSchema).nullable(),
	receiptId: z.lazy(() => AttachmentIdSchema).nullable(),
	attachments: z.array(z.lazy(() => AttachmentIdSchema)).optional(),
	kid: z.string().nullable().optional(),
	payToAccount: z.string().nullable().optional(),
	isIgnored: z.boolean(),
	hasMatchProblem: z.boolean().optional(),
	appliedRules: z.array(z.lazy(() => AutoTransactionInfoDtoSchema)),
	matchingRules: z.array(z.lazy(() => AutoTransactionInfoDtoSchema)),
});
export type TransactionListDto = z.infer<typeof TransactionListDtoSchema>;

export const PreviewMatchesResponseDtoSchema = z.object({
	matches: z.array(z.lazy(() => TransactionListDtoSchema)),
	truncated: z.boolean(),
});
export type PreviewMatchesResponseDto = z.infer<typeof PreviewMatchesResponseDtoSchema>;

export const ConditionEntityHitsDtoSchema = z.object({
	path: z.array(z.number().int()),
	kind: z.string(),
	matched: z.array(z.string()),
	hidden: z.array(z.string()),
	error: z.string().nullable(),
});
export type ConditionEntityHitsDto = z.infer<typeof ConditionEntityHitsDtoSchema>;

export const PreviewEntitiesResponseDtoSchema = z.object({
	conditions: z.array(z.lazy(() => ConditionEntityHitsDtoSchema)),
});
export type PreviewEntitiesResponseDto = z.infer<typeof PreviewEntitiesResponseDtoSchema>;

export const MatchSetDtoSchema = z.object({
	id: z.lazy(() => MatchSetIdSchema),
	name: z.string(),
	description: z.string().nullable(),
	condition: z.lazy(() => ConditionDtoSchema),
	usedBy: z.lazy(() => LibraryUsageDtoSchema),
	problems: z.array(z.lazy(() => RuleProblemDtoSchema)),
});
export type MatchSetDto = z.infer<typeof MatchSetDtoSchema>;

export const NewMatchSetDtoSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
	condition: z.lazy(() => ConditionDtoSchema),
});
export type NewMatchSetDto = z.infer<typeof NewMatchSetDtoSchema>;

export const UpdateMatchSetDtoSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
	condition: z.lazy(() => ConditionDtoSchema),
});
export type UpdateMatchSetDto = z.infer<typeof UpdateMatchSetDtoSchema>;

export const ActionSetInputSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
});
export type ActionSetInput = z.infer<typeof ActionSetInputSchema>;

export const ActionSetDtoSchema = z.object({
	id: z.lazy(() => ActionSetIdSchema),
	name: z.string(),
	description: z.string().nullable(),
	inputs: z.array(z.lazy(() => ActionSetInputSchema)),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
	usedBy: z.lazy(() => LibraryUsageDtoSchema),
	problems: z.array(z.lazy(() => RuleProblemDtoSchema)),
});
export type ActionSetDto = z.infer<typeof ActionSetDtoSchema>;

export const NewActionSetDtoSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
	inputs: z.array(z.lazy(() => ActionSetInputSchema)),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
});
export type NewActionSetDto = z.infer<typeof NewActionSetDtoSchema>;

export const UpdateActionSetDtoSchema = z.object({
	name: z.string(),
	description: z.string().nullable(),
	inputs: z.array(z.lazy(() => ActionSetInputSchema)),
	actions: z.array(z.lazy(() => EditActionDtoSchema)),
});
export type UpdateActionSetDto = z.infer<typeof UpdateActionSetDtoSchema>;

export const MatchProblemCountDtoSchema = z.object({
	rule: z.lazy(() => AutoTransactionInfoDtoSchema).nullable(),
	field: z.lazy(() => FieldNameSchema),
	value: z.string(),
	transactionCount: z.number().int(),
});
export type MatchProblemCountDto = z.infer<typeof MatchProblemCountDtoSchema>;

export const MatchProblemSummaryDtoSchema = z.object({
	counts: z.array(z.lazy(() => MatchProblemCountDtoSchema)),
	transactionCount: z.number().int(),
});
export type MatchProblemSummaryDto = z.infer<typeof MatchProblemSummaryDtoSchema>;

export const IndexErrorRowDtoSchema = z.object({
	storedTxId: z.lazy(() => StoredTxIdSchema),
	error: z.string(),
	importId: z.lazy(() => ImportIdSchema).nullable(),
	datePaid: z.lazy(() => DateStringSchema).nullable(),
	merchant: z.string().nullable(),
});
export type IndexErrorRowDto = z.infer<typeof IndexErrorRowDtoSchema>;

export const RebuildStatusDtoSchema = z.object({
	logicVersion: z.number().int(),
	isRebuilding: z.boolean(),
	progress: z.number().int(),
	total: z.number().int(),
	errorRowCount: z.number().int(),
	errorRows: z.array(z.lazy(() => IndexErrorRowDtoSchema)),
});
export type RebuildStatusDto = z.infer<typeof RebuildStatusDtoSchema>;

export const ServerStatusDtoSchema = z.object({
	version: z.lazy(() => VersionInfoDtoSchema),
	rebuildStatus: z.lazy(() => RebuildStatusDtoSchema),
});
export type ServerStatusDto = z.infer<typeof ServerStatusDtoSchema>;

export const AttachmentTypeSchema = z.enum(["image/webp", "application/pdf"]);
export type AttachmentType = z.infer<typeof AttachmentTypeSchema>;

export const AttachmentDtoSchema = z.object({
	id: z.lazy(() => AttachmentIdSchema),
	filename: z.string(),
	contentType: z.lazy(() => AttachmentTypeSchema),
	size: z.number().int(),
	createdAt: z.lazy(() => DateTimeStringSchema),
	notes: z.string().nullable().optional(),
	previewVersion: z.number().int(),
	hasInvoice: z.boolean(),
	hasTransaction: z.boolean(),
});
export type AttachmentDto = z.infer<typeof AttachmentDtoSchema>;

export const UpdateAttachmentDtoSchema = z.object({
	filename: z.string(),
	notes: z.string().nullable().optional(),
});
export type UpdateAttachmentDto = z.infer<typeof UpdateAttachmentDtoSchema>;

export const TransactionPageDtoSchema = z.object({
	items: z.array(z.lazy(() => TransactionListDtoSchema)),
	nextCursor: z.string().nullable(),
	hasMore: z.boolean(),
});
export type TransactionPageDto = z.infer<typeof TransactionPageDtoSchema>;

export const SourceEntitySchema = z.object({
	id: z.lazy(() => SourceIdSchema),
	name: z.lazy(() => SourceSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type SourceEntity = z.infer<typeof SourceEntitySchema>;

export const GroupEntitySchema = z.object({
	id: z.lazy(() => GroupIdSchema),
	name: z.lazy(() => GroupSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type GroupEntity = z.infer<typeof GroupEntitySchema>;

export const CategoryEntitySchema = z.object({
	id: z.lazy(() => CategoryIdSchema),
	name: z.lazy(() => CategorySchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type CategoryEntity = z.infer<typeof CategoryEntitySchema>;

export const ExpenseTypeEntitySchema = z.object({
	id: z.lazy(() => ExpenseTypeIdSchema),
	name: z.lazy(() => ExpenseTypeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type ExpenseTypeEntity = z.infer<typeof ExpenseTypeEntitySchema>;

export const PayeeEntitySchema = z.object({
	id: z.lazy(() => PayeeIdSchema),
	name: z.lazy(() => PayeeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	created: z.lazy(() => DateTimeStringSchema),
	updated: z.lazy(() => DateTimeStringSchema),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type PayeeEntity = z.infer<typeof PayeeEntitySchema>;

export const NewSourceEntitySchema = z.object({
	name: z.lazy(() => SourceSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type NewSourceEntity = z.infer<typeof NewSourceEntitySchema>;

export const NewGroupEntitySchema = z.object({
	name: z.lazy(() => GroupSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type NewGroupEntity = z.infer<typeof NewGroupEntitySchema>;

export const NewCategoryEntitySchema = z.object({
	name: z.lazy(() => CategorySchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type NewCategoryEntity = z.infer<typeof NewCategoryEntitySchema>;

export const NewExpenseTypeEntitySchema = z.object({
	name: z.lazy(() => ExpenseTypeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type NewExpenseTypeEntity = z.infer<typeof NewExpenseTypeEntitySchema>;

export const NewPayeeEntitySchema = z.object({
	name: z.lazy(() => PayeeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type NewPayeeEntity = z.infer<typeof NewPayeeEntitySchema>;

export const UpdateSourceEntitySchema = z.object({
	id: z.lazy(() => SourceIdSchema),
	name: z.lazy(() => SourceSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type UpdateSourceEntity = z.infer<typeof UpdateSourceEntitySchema>;

export const UpdateGroupEntitySchema = z.object({
	id: z.lazy(() => GroupIdSchema),
	name: z.lazy(() => GroupSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type UpdateGroupEntity = z.infer<typeof UpdateGroupEntitySchema>;

export const UpdateCategoryEntitySchema = z.object({
	id: z.lazy(() => CategoryIdSchema),
	name: z.lazy(() => CategorySchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type UpdateCategoryEntity = z.infer<typeof UpdateCategoryEntitySchema>;

export const UpdateExpenseTypeEntitySchema = z.object({
	id: z.lazy(() => ExpenseTypeIdSchema),
	name: z.lazy(() => ExpenseTypeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type UpdateExpenseTypeEntity = z.infer<typeof UpdateExpenseTypeEntitySchema>;

export const UpdatePayeeEntitySchema = z.object({
	id: z.lazy(() => PayeeIdSchema),
	name: z.lazy(() => PayeeSchema),
	description: z.string().nullable(),
	colorBg: z.string().nullable(),
	colorFg: z.string().nullable(),
	style: z.lazy(() => EntityStyleSchema).nullable(),
	priority: z.number().int().optional(),
	hidden: z.boolean().optional(),
	accountNumber: z.string().nullable().optional(),
	kind: z.lazy(() => AccountKindSchema).nullable().optional(),
});
export type UpdatePayeeEntity = z.infer<typeof UpdatePayeeEntitySchema>;

export type Entity<Id, Name> = {
  id: Id;
  name: Name;
  description: string | null;
  colorBg: string | null;
  colorFg: string | null;
  style: EntityStyle | null;
  priority?: number;
  created: DateTimeString;
  updated: DateTimeString;
  hidden?: boolean;
  /** Account registry only; absent on the label registries. */
  accountNumber?: string | null;
  kind?: AccountKind | null;
};

export type NewEntity<Name> = {
  name: Name;
  description: string | null;
  colorBg: string | null;
  colorFg: string | null;
  style: EntityStyle | null;
  priority?: number;
  hidden?: boolean;
  /** Account registry only; ignored by the label registries. */
  accountNumber?: string | null;
  kind?: AccountKind | null;
};

export type UpdateEntity<Id, Name> = {
  id: Id;
  name: Name;
  description: string | null;
  colorBg: string | null;
  colorFg: string | null;
  style: EntityStyle | null;
  priority?: number;
  hidden?: boolean;
  /** Account registry only; ignored by the label registries. */
  accountNumber?: string | null;
  kind?: AccountKind | null;
};

export function createId<T extends string>(id: string): T {
  return id as T;
}

export const CURRENCY_EXPONENTS: Record<string, number> = {
  NOK: 2,
  SEK: 2,
  USD: 2,
  EUR: 2,
  DKK: 2,
  AED: 2,
  THB: 2,
};
export const currencyExponent = (code: string): number =>
  CURRENCY_EXPONENTS[code] ?? 2;

export const ROUTE_MAPPINGS = [
  "/api/admin/rebuild-index",
  "/api/admin/rebuild-status",
  "/api/attachments",
  "/api/attachments/{id}",
  "/api/attachments/{id}/content",
  "/api/attachments/{id}/preview",
  "/api/config",
  "/api/import",
  "/api/imports",
  "/api/imports/{id}",
  "/api/imports/{id}/apply/{pending_id}",
  "/api/imports/{id}/csv",
  "/api/imports/{id}/diff",
  "/api/imports/{id}/diff/rerun",
  "/api/imports/{id}/diff/{pending_id}",
  "/api/invoice-detectors",
  "/api/lists/categories",
  "/api/lists/categories/{id}",
  "/api/lists/expense-types",
  "/api/lists/expense-types/{id}",
  "/api/lists/groups",
  "/api/lists/groups/{id}",
  "/api/lists/invoices",
  "/api/lists/invoices/{id}",
  "/api/lists/payees",
  "/api/lists/payees/{id}",
  "/api/lists/sources",
  "/api/lists/sources/{id}",
  "/api/logo/{kind}/{id}",
  "/api/registry",
  "/api/settings/auto-transactions",
  "/api/settings/auto-transactions/preview-entities",
  "/api/settings/auto-transactions/preview-matches",
  "/api/settings/auto-transactions/{id}",
  "/api/settings/library/action-sets",
  "/api/settings/library/action-sets/{id}",
  "/api/settings/library/action-sets/{id}/usage",
  "/api/settings/library/match-sets",
  "/api/settings/library/match-sets/{id}",
  "/api/settings/library/match-sets/{id}/usage",
  "/api/status",
  "/api/summary",
  "/api/transactions",
  "/api/transactions/bulk",
  "/api/transactions/export",
  "/api/transactions/match-problems",
  "/api/transactions/month-summary",
  "/api/transactions/{id}/details",
  "/api/transactions/{id}/edit",
  "/api/transactions/{id}/preview",
  "/api/transactions/{id}/reset"
];
